import React from 'react';

import { Table as BsaTable } from 'bsa-ui';
import cn from 'classnames';
import 'handy-scroll';
import { isNull } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { personGuidSel, setTablePageSize, tablePageSizeSel } from '../../duck';
import CustomRow from './CustomRow';
import styles from './Table.less';
import { getItemsPresentation } from './helpers';

const locale = { emptyText: <FormattedMessage id="shared.Table.noData" /> };

class Table extends React.PureComponent {
  state = {
    activePage: 1,
    scrollReady: false,
    scrollId: `id${uuidv4().split('-')[0]}`,
  };

  componentDidMount() {
    this.observer = new MutationObserver(() => this.setScrollId());
    this.observer.observe(this.ref, {
      childList: true,
      subtree: true,
    });
  }

  componentDidUpdate({ sorter: prevSorter }) {
    const { sorter: currentSorter } = this.props;

    if (
      prevSorter.columnKey !== currentSorter.columnKey &&
      prevSorter.order !== currentSorter.order
    ) {
      this.setState({ activePage: 1 });
    }
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  async setScrollId() {
    const { scrollId } = this.state;
    const scrollIdSelector = `${scrollId}-scroll`;
    try {
      const { floatingHorizontalScroll } = this.props;

      if (
        !floatingHorizontalScroll ||
        !isNull(document.getElementById(scrollIdSelector))
      ) {
        return;
      }

      const scrollEl = document.querySelector(
        `#${scrollId}-container .ant-table-scroll .ant-table-body`,
      );

      if (scrollEl) {
        scrollEl.id = scrollIdSelector;
        this.setState({ scrollReady: true });
        this.observer.disconnect();
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      this.setState({ scrollReady: false });
    }
  }

  preparePagination = pagination => {
    if (pagination) {
      if (this.props.loading) {
        this.setState({ activePage: 1 });
      }
      const { pageSize } = this.props;
      const { activePage } = this.state;

      return {
        current: activePage,
        showSizeChanger: true,
        pageSizeOptions: ['10', '25', '50', '75', '100'],
        pageSize,
        onShowSizeChange: this.handleShowSizeChange,
        onChange: this.handlePageChange,
        ...pagination,
      };
    }

    return pagination;
  };

  handleShowSizeChange = (_, pageSize) => {
    this.props.onSetPageSize(pageSize, this.props.personGuid);
  };

  handlePageChange = page => {
    this.setState({ activePage: page });
  };

  getCustomRow = () => {
    const { mainRows } = this.props;
    if (!mainRows) {
      return undefined;
    }
    return { body: { row: CustomRow } };
  };

  itemsTransformation = memoize((items, CustomMainRow, colSpan) =>
    getItemsPresentation(
      items.map(item => ({ ...item, CustomMainRow, colSpan })),
    ),
  );

  render() {
    const {
      headerColor,
      rowPadding,
      fixedLayout,
      clickableRows,
      showExpandRowColumn,
      expandedRowPadding,
      expandedRowKeys,
      expandedRowRender,
      border,
      className,
      pagination,
      dataSource,
      mainRows,
      floatingHorizontalScroll,
      ...otherProps
    } = this.props;
    const classNames = cn(
      className,
      styles.table,
      styles[`header-${headerColor}`],
      styles[`rowPadding_${rowPadding}`],
      {
        [styles.withHiddenExpandColumn]:
          !!expandedRowRender && !showExpandRowColumn,
        [styles.fixedLayout]: fixedLayout,
        [styles.clickableRows]: clickableRows,
        [styles.hideExpandRowColumn]: !showExpandRowColumn,
        [styles.expandedRowNoPad]: !expandedRowPadding,
        [styles.noBorder]: !border,
      },
    );

    // Table can mutate this array, so if we pass in a value from redux store,
    // we can end up with an error (can't mutate store values)
    // so here we're copying the array to avoid this
    if (expandedRowKeys) {
      otherProps.expandedRowKeys = [...expandedRowKeys];
    }

    const { scrollReady } = this.state;
    const itemsPresentation = mainRows
      ? this.itemsTransformation(
          dataSource,
          mainRows.CustomMainRow,
          mainRows.colSpan,
        )
      : dataSource;

    return (
      <div
        id={`${this.state.scrollId}-container`}
        ref={ref => (this.ref = ref)}
      >
        <BsaTable
          className={classNames}
          locale={locale}
          expandedRowRender={expandedRowRender}
          pagination={this.preparePagination(pagination)}
          dataSource={itemsPresentation}
          components={this.getCustomRow()}
          {...otherProps}
        />
        {floatingHorizontalScroll && scrollReady && (
          <handy-scroll
            owner={`${this.state.scrollId}-scroll`}
            class={styles.handyScroll}
          />
        )}
      </div>
    );
  }
}

Table.propTypes = {
  // provided by parent
  ...BsaTable.propTypes,
  className: PropTypes.string,
  headerColor: PropTypes.oneOf([
    'scouting-red',
    'scouting-warm-gray',
    'gray-5',
    'gray-1',
  ]),
  rowPadding: PropTypes.oneOf(['large', 'medium', 'small']),
  fixedLayout: PropTypes.bool,
  clickableRows: PropTypes.bool,
  showExpandRowColumn: PropTypes.bool,
  expandedRowPadding: PropTypes.bool,
  expandedRowRender: PropTypes.func,
  border: PropTypes.bool,
  sorter: PropTypes.object,
  mainRows: PropTypes.shape({
    CustomMainRow: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
      .isRequired,
    colSpan: PropTypes.number.isRequired,
  }),
  // provided by component itself
  pageSize: PropTypes.number.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  personGuid: PropTypes.string.isRequired,
  floatingHorizontalScroll: PropTypes.bool,
};

Table.defaultProps = {
  headerColor: 'scouting-red',
  rowPadding: 'medium',
  fixedLayout: true,
  showExpandRowColumn: false,
  expandedRowPadding: false,
  border: true,
  sorter: {},
  floatingHorizontalScroll: false,
};

const mapState = state => ({
  pageSize: tablePageSizeSel(state),
  personGuid: personGuidSel(state),
});

const mapDispatch = dispatch => ({
  onSetPageSize: (pageSize, personGuid) =>
    dispatch(setTablePageSize(pageSize, personGuid)),
});

export default connect(mapState, mapDispatch)(Table);
